/**
 * Drop technical "Send only one of fieldId..." tails from backend validation copy.
 * @param {string} text
 * @returns {string}
 */
function stripTechnicalFieldSuffix(text) {
  if (!text || typeof text !== "string") return "";
  const cut = text.search(/\.\s*Send only one of\b/i);
  if (cut !== -1) {
    return text.slice(0, cut).trim();
  }
  return text.trim();
}

/**
 * Combine top-level `message` with `errors` field messages from API validation responses.
 * @param {object|null|undefined} data - Typically axios err.response.data
 * @param {(s: string) => string} translate - Optional i18n pass-through (e.g. translateError)
 * @returns {string}
 */
export function validationMessageFromApiData(data, translate = (s) => s) {
  if (!data || typeof data !== "object") return "";

  const primary =
    (data.message && String(data.message).trim()) ||
    (typeof data.error === "string" && data.error.trim() ? data.error.trim() : "");
  const topMessage = stripTechnicalFieldSuffix(
    (translate(primary) || "").trim()
  );
  const errorsObj = data.errors;

  if (errorsObj && typeof errorsObj === "object" && !Array.isArray(errorsObj)) {
    const uniqueDetails = [
      ...new Set(
        Object.values(errorsObj)
          .map((v) =>
            typeof v === "string"
              ? stripTechnicalFieldSuffix(
                  (translate(v.trim()) || "").trim()
                )
              : ""
          )
          .filter(Boolean)
      ),
    ];

    if (uniqueDetails.length > 0) {
      const detail = uniqueDetails[0];
      if (topMessage && detail) {
        const base = topMessage.replace(/\.\s*$/, "");
        return `${base}. ${detail}`;
      }
      return uniqueDetails.join(" ");
    }
  }

  return topMessage;
}

function isGenericServerError(text) {
  return /^server error\b/i.test(String(text || "").trim());
}

/**
 * Branch-limit failures currently come back as
 * `{ message: "Server error", error: "This subscription includes …" }`.
 * Return that `error` sentence so the UI can show it.
 * @param {object|null|undefined} data
 * @param {(key: string, options?: object) => string} t
 * @returns {string}
 */
export function subscriptionConnectErrorMessage(data, t) {
  if (!data || typeof data !== "object") return "";
  const message = typeof data.message === "string" ? data.message.trim() : "";
  const detail = typeof data.error === "string" ? data.error.trim() : "";
  const text = isGenericServerError(message) ? detail : (
    /includes \d+ branch|not on the active subscription|subscription is suspended|active subscription is required/i.test(message)
      ? message
      : ""
  );
  if (!text) return "";
  return typeof t === "function" ? t(text, { defaultValue: text }) : text;
}
